type UploadFileGenUrlResult = {
  objectId: string;
  uploadUrl: string;
  fileUrl: string;
}

/**
 * Required schema from API Gateway URL:
 *
 * Get an upload URL to upload an attachment
 *  {
 *      "filename": "myfile.jpg"
 *      "isReplace": true  // optional, default true
 *  }
 *  Returns:
 *  {
 *      "objectId": "objectId",
 *      "uploadUrl": "uploadUrl"  // Presigned URL for S3 upload
 *  }
 *
 * @param url An API Gateway URL endpoint that generates presigned S3 URL.
 * @param f File to be uploaded
 * @returns Object ID of the uploaded file, or null if failed
 */
export default async function handleUploadFile(url: string, f: File, isReplace?: boolean):
    Promise<string | null> {
  // Legacy compatibility
  const result = await handleUploadFileWithResult(url, f, isReplace);
  return result?.objectId ?? null;
}

export async function handleUploadFileWithResult(url: string, f: File, isReplace?: boolean):
    Promise<UploadFileGenUrlResult | null> {
  console.log("file", f);
  const filename = f.name;
  const body = { filename };
  if (isReplace !== undefined) {
    (body as any).isReplace = isReplace;
  }
  const resGenUrl = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!resGenUrl.ok) {
    return null;
  }

  const result = await resGenUrl.json();
  const resUpload = await fetch(result.uploadUrl, {
    method: "PUT",
    body: f,
  });
  if (!resUpload.ok) {
    return null;
  }
  return result;
}

/**
 * Bulk upload files by first requesting pre-signed URLs in bulk,
 * then uploading each file to its corresponding pre-signed URL.
 */
export async function handleBulkUploadFiles(
  url: string,
  files: File[],
  isReplace?: boolean
): Promise<{ success: number; failed: number }> {
  if (files.length === 0) return { success: 0, failed: 0 };

  const filenames = files.map((f) => f.name);
  const body: any = { filenames };
  if (isReplace !== undefined) {
    body.isReplace = isReplace;
  }

  // 1. Get presigned URLs for all files
  const resGenUrl = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!resGenUrl.ok) {
    throw new Error("Failed to generate upload URLs");
  }

  const results: any[] = await resGenUrl.json();
  
  let successCount = 0;
  let failedCount = 0;

  // 2. Upload each file to its presigned URL
  for (const file of files) {
    // Find the corresponding presigned URL result
    const result = results.find((r) => r.filename === file.name);
    if (!result || !result.uploadUrl) {
      console.error(`No upload URL found for file: ${file.name}`);
      failedCount++;
      continue;
    }

    try {
      const resUpload = await fetch(result.uploadUrl, {
        method: "PUT",
        body: file,
      });

      if (resUpload.ok) {
        successCount++;
      } else {
        console.error(`Failed to upload ${file.name} to S3: ${resUpload.statusText}`);
        failedCount++;
      }
    } catch (err) {
      console.error(`Error uploading ${file.name}:`, err);
      failedCount++;
    }
  }

  return { success: successCount, failed: failedCount };
}